import React from "react";
import { Typography, Tag, Tooltip } from "antd";
import {
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { RoundStatistics } from "./useAssessmentStatistics";
import { CHART_COLORS } from "../../../JobPostDetails/components/StatsAndAnalytics/utils/chartColors";

interface AssessmentRoundCardProps {
  round: RoundStatistics;
  isSelected: boolean;
  onClick: () => void;
  statusFilter: "all" | "qualified" | "unqualified" | "pending";
  onQuestionFilterChange?: (filter: "all" | "qualified" | "unqualified" | "pending") => void;
  /** When true, card is not clickable (no attempt data available for this round). */
  disabled?: boolean;
}

const AssessmentRoundCard: React.FC<AssessmentRoundCardProps> = ({
  round,
  isSelected,
  onClick,
  statusFilter,
  onQuestionFilterChange,
  disabled = false,
}) => {
  const statusConfig = {
    qualified: { color: "success", icon: <CheckCircleOutlined />, label: "Qualified" },
    unqualified: { color: "error", icon: <CloseCircleOutlined />, label: "Unqualified" },
    pending: { color: "processing", icon: <ClockCircleOutlined />, label: "Pending" },
  };

  const currentStatus = statusConfig[round.status] || statusConfig.pending;

  // Calculate percentage: qualified answers / total questions
  const qualifiedPercentage = round.totalQuestions > 0 
    ? Math.round((round.qualifiedQuestions / round.totalQuestions) * 100) 
    : 0;

  const remainingPercentage = 100 - qualifiedPercentage;

  // Doughnut chart data: qualified vs remaining
  const chartData = round.totalQuestions === 0
    ? [{ name: "No Data", value: 100, color: CHART_COLORS.placeholder }]
    : [
        { name: "Qualified", value: qualifiedPercentage, color: CHART_COLORS.success },
        { name: "Remaining", value: remainingPercentage, color: "#e8e8e8" },
      ];

  const filterButtons = [
    { key: "all" as const, label: "All", count: round.totalQuestions },
    { key: "qualified" as const, label: "Qualified", count: round.qualifiedQuestions, color: CHART_COLORS.success },
    { key: "unqualified" as const, label: "Unqualified", count: round.unqualifiedQuestions, color: CHART_COLORS.error },
    { key: "pending" as const, label: "Pending", count: round.pendingQuestions, color: CHART_COLORS.warning },
  ];

  const handleFilterClick = (e: React.MouseEvent, filter: "all" | "qualified" | "unqualified" | "pending") => {
    e.stopPropagation();
    onQuestionFilterChange?.(filter);
  };

  return (
    <Tooltip
      title={disabled ? "No attempt data available for this round" : undefined}
    >
      <div
        className={`assessment-round-card ${isSelected ? "assessment-round-card-selected" : ""} ${disabled ? "assessment-round-card-disabled" : ""}`}
        onClick={disabled ? undefined : onClick}
        role={disabled ? undefined : "button"}
        aria-disabled={disabled}
      >
      <div className="assessment-round-card-header">
        <div className="assessment-round-card-title">
          <Typography.Text strong style={{ fontSize: 13 }} ellipsis={{ tooltip: true }}>
            <Tooltip title={round.roundName}>
              <span>{round.roundName}</span>
            </Tooltip>
          </Typography.Text>
          <div className="assessment-round-card-badges">
            {/* Show attempt badge if this round has been restarted */}
            {round.isRestarted && (
              <Tooltip title={`This is attempt ${round.attemptNumber} for this round`}>
                <Tag color="purple" icon={<ReloadOutlined />} style={{ fontSize: 10, margin: 0 }}>
                  Attempt {round.attemptNumber}
                </Tag>
              </Tooltip>
            )}
            <Tag color={currentStatus.color as any} style={{ fontSize: 10, margin: 0 }}>
              {currentStatus.label}
            </Tag>
            {round.hasViolations && (
              <Tooltip title={`${round.violationCount} violation(s) detected`}>
                <Tag color="warning" icon={<WarningOutlined />} style={{ fontSize: 10, margin: 0 }}>
                  {round.violationCount}
                </Tag>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      <div className="assessment-round-card-content">
        <div className="assessment-round-card-chart">
          <ResponsiveContainer width="100%" height={50}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={14}
                outerRadius={22}
                paddingAngle={0}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <text 
                x="50%" 
                y="50%" 
                textAnchor="middle" 
                dominantBaseline="middle"
                style={{ fontSize: 10, fontWeight: 600, fill: CHART_COLORS.success }}
              >
                {qualifiedPercentage}%
              </text>
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="assessment-round-card-stats">
          <div className="assessment-round-card-stat-row">
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Questions: <strong>{round.totalQuestions}</strong>
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Attempted: <strong>{round.attemptedQuestions}</strong>
            </Typography.Text>
          </div>
          <div className="assessment-round-card-stat-row">
            <div className="assessment-round-card-mini-stat" style={{ backgroundColor: `${CHART_COLORS.success}20` }}>
              <CheckCircleOutlined style={{ color: CHART_COLORS.success, fontSize: 11 }} />
              <span style={{ fontSize: 11, color: CHART_COLORS.success }}>{round.qualifiedQuestions}</span>
            </div>
            <div className="assessment-round-card-mini-stat" style={{ backgroundColor: `${CHART_COLORS.error}20` }}>
              <CloseCircleOutlined style={{ color: CHART_COLORS.error, fontSize: 11 }} />
              <span style={{ fontSize: 11, color: CHART_COLORS.error }}>{round.unqualifiedQuestions}</span>
            </div>
            {/* <div className="assessment-round-card-mini-stat" style={{ backgroundColor: `${CHART_COLORS.warning}20` }}>
              <ClockCircleOutlined style={{ color: CHART_COLORS.warning, fontSize: 11 }} />
              <span style={{ fontSize: 11, color: CHART_COLORS.warning }}>{round.pendingQuestions}</span>
            </div> */}
          </div>
        </div>
      </div>

      {/* Commented out filter buttons - now handled in Questions section header */}
      {/* {isSelected && onQuestionFilterChange && (
        <div className="assessment-round-card-filters" onClick={(e) => e.stopPropagation()}>
          {filterButtons.map((btn) => (
            <button
              key={btn.key}
              className={`assessment-filter-chip ${statusFilter === btn.key ? "active" : ""}`}
              onClick={(e) => handleFilterClick(e, btn.key)}
              style={
                statusFilter === btn.key && btn.color
                  ? { borderColor: btn.color, backgroundColor: `${btn.color}15` }
                  : {}
              }
            >
              {btn.label} ({btn.count})
            </button>
          ))}
        </div>
      )} */}
      </div>
    </Tooltip>
  );
};

export default AssessmentRoundCard;



